/**
 * Frame reducer for the terminal state machine — ported from the parked
 * `AgentProvider.tsx` and rewired onto `@paradigm/terminal-core` types.
 *
 * `handleFrame(frame, setState, options)` is the single entry point the
 * provider wires to `AgentWebSocket.onMessage`. It owns all gateway-frame →
 * chat-state translation: streaming deltas, structured `chat` events,
 * widgets / agent-specs, approvals, generative-UI render directives, app
 * register/remove, processing-state indicators, and `/model` echo
 * suppression. Per-session routing (runId → owning session + background
 * buffering) is injected via `options.routing` so the same handler works in
 * unit tests (single-session fallback) and in the live provider.
 */
import type {
  ApprovalRequest,
  ChatMessage,
  GatewayFrame,
  UiRender,
} from '@paradigm/terminal-core';
import { generateMessageId, isSafeAppUrl } from '@paradigm/terminal-core';
import type { Dispatch, SetStateAction } from 'react';

import type { AgentState } from './contexts';

/**
 * Manifest for a custom application tab registered by the agent (via the
 * `app.register` event). The new core does not export this type, so the
 * minimal shape the handler needs is declared here and surfaced through the
 * `onAppRegister` plumbing.
 */
export interface AppManifest {
  readonly id: string;
  readonly name: string;
  readonly icon?: string;
  readonly url: string;
  readonly permissions: readonly string[];
}

/**
 * Per-session routing for message-producing frames. When omitted (e.g. in
 * unit tests), all frames apply to whatever state `setState` exposes —
 * preserving the pre-routing single-session behaviour. The provider wires
 * this in for real so an in-flight run keeps writing to its original
 * session even after the user clicks `+ New chat`.
 */
export interface FrameRouting {
  readonly routeMessageFrame: (
    payload: { sessionKey?: string; runId?: string },
    reducer: (messages: readonly ChatMessage[]) => readonly ChatMessage[],
    activeExtra?: {
      processing?: { label: string; detail?: string } | null;
    },
  ) => void;
  readonly isCancelled: (runId: string | undefined) => boolean;
  readonly isFrameForActiveSession: (payload: {
    sessionKey?: string;
    runId?: string;
  }) => boolean;
  /**
   * Signal that `runId` has reached a terminal state (done / error /
   * terminal `data.state`). The provider uses this hook to drop the
   * runId from its run-ownership map so it doesn't accumulate one entry
   * per run for the lifetime of the page.
   */
  readonly onRunEnd?: (runId: string) => void;
}

export interface HandleFrameOptions {
  readonly generativeUiEnabled: boolean;
  /**
   * Called when the gateway dispatches an `app.register` event for a
   * custom application tab.
   */
  readonly onAppRegister?: (manifest: AppManifest) => void;
  /** Called when the gateway dispatches an `app.remove` event. */
  readonly onAppRemove?: (id: string) => void;
  readonly routing?: FrameRouting;
  /**
   * Mutated set of runIds whose first assistant delta was a gateway echo
   * of `/model <id>` — see the provider's `setSelectedModel`. The picker
   * shows an optimistic ack client-side; the echo would just duplicate it.
   * Optional so unit tests that don't care about the picker still work.
   */
  readonly suppressedModelEchoRunIds?: Set<string>;
  /**
   * Called for orphan error responses (no matching pending request) that are
   * not rendered as chat messages — e.g. "gateway unreachable". The host can
   * wire this to a transient toast / notification surface.
   */
  readonly onTransientError?: (message: string) => void;
}

const PROCESSING_STATE_LABELS: Readonly<Record<string, string>> = {
  thinking: 'Thinking',
  reasoning: 'Thinking',
  planning: 'Planning',
  running: 'Running',
  tool_use: 'Calling tool',
  tool_call: 'Calling tool',
  tool: 'Calling tool',
  searching: 'Searching',
  analyzing: 'Analyzing',
  streaming: 'Writing',
  writing: 'Writing',
};

/** `agent.data.state` values that mean "stop showing the indicator." */
const TERMINAL_PROCESSING_STATES: ReadonlySet<string> = new Set([
  'done',
  'complete',
  'completed',
  'finished',
  'idle',
  'ready',
]);

/**
 * Turn a raw gateway state string into a display-ready label.
 *
 * `tool_use-running ` → `Tool Use Running`
 *   1. `[_-]+` → ` `   collapse snake_case / kebab-case separators to spaces
 *   2. `\s+`   → ` `   collapse any runs of whitespace the above produced
 *   3. `.trim()`       drop leading/trailing whitespace
 *   4. `\b\w` → upper  title-case the first letter of every word
 */
function humanizeState(state: string): string {
  return state
    .replace(/[_-]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
    .replace(/\b\w/gu, (c) => c.toUpperCase());
}

function resolveProcessingLabel(state: string): string {
  const key = state.toLowerCase();
  return PROCESSING_STATE_LABELS[key] ?? humanizeState(state);
}

/**
 * True if `text` looks like the gateway's echo of a `/model <id>` switch
 * (e.g. `Model set to … for this session.` or a bare `/model …` echo). The
 * picker already shows an optimistic acknowledgement client-side, so the
 * echoed bubble is dropped. Pure string detection — not catalog data — so
 * it lives in the handler rather than the host's model capability.
 */
function isModelSwitchEcho(text: string): boolean {
  const trimmed = text.trimStart();
  if (trimmed.startsWith('/model')) return true;
  return /^\s*model\s+set\s+to\b|^\s*model\s+reset\s+to\s+default\b|^\s*switched\s+to\s+model\b/iu.test(
    text,
  );
}

/**
 * Flip any `isStreaming: true` messages to `false`. The gateway's
 * `agent.done` / `chat.done` event is the canonical stream terminator,
 * but we can't rely on it — a dropped frame, a run-id flip, or a skipped
 * done event would leave the streaming dots animating forever. Call this
 * on every other concrete terminator (terminal state, new widget, new
 * approval, error, new streaming run) so the UI self-corrects.
 */
function closeStreamingMessages(
  messages: readonly ChatMessage[],
  runId?: string,
): readonly ChatMessage[] {
  if (
    !messages.some(
      (m) => m.isStreaming === true && (runId == null || m.runId === runId),
    )
  ) {
    return messages;
  }
  return messages.map((m) =>
    m.isStreaming === true && (runId == null || m.runId === runId)
      ? { ...m, isStreaming: false }
      : m,
  );
}

function updateStreamingMessage(
  messages: readonly ChatMessage[],
  runId: string | undefined,
  update: (message: ChatMessage) => ChatMessage,
): readonly ChatMessage[] | null {
  const index = messages.findIndex(
    (m) =>
      m.role === 'assistant' &&
      m.isStreaming === true &&
      (runId == null ? m.runId == null : m.runId === runId),
  );

  if (index === -1) return null;

  return [
    ...messages.slice(0, index),
    update(messages[index]!),
    ...messages.slice(index + 1),
  ];
}

function replaceRunMessages(
  messages: readonly ChatMessage[],
  runId: string | undefined,
  replacements: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (runId == null)
    return [...closeStreamingMessages(messages), ...replacements];

  const next: ChatMessage[] = [];
  let inserted = false;

  for (const message of messages) {
    const isSameRun = message.role === 'assistant' && message.runId === runId;
    if (!isSameRun) {
      next.push(message);
      continue;
    }

    if (!inserted) {
      next.push(...replacements);
      inserted = true;
    }
  }

  if (!inserted) next.push(...replacements);

  return next;
}

export function handleFrame(
  frame: GatewayFrame,
  setState: Dispatch<SetStateAction<AgentState>>,
  options: HandleFrameOptions = { generativeUiEnabled: false },
) {
  // Fallbacks let `handleFrame` keep working without per-session routing
  // (unit tests, etc.) — in that mode every frame applies to current state.
  const routeMessageFrame: FrameRouting['routeMessageFrame'] =
    options.routing?.routeMessageFrame ??
    ((_payload, reducer, activeExtra) => {
      setState((prev) => {
        const next = reducer(prev.messages);
        if (
          next === prev.messages &&
          (activeExtra == null || !('processing' in activeExtra))
        ) {
          return prev;
        }
        return {
          ...prev,
          messages: next,
          ...(activeExtra != null && 'processing' in activeExtra
            ? { processing: activeExtra.processing ?? null }
            : {}),
        };
      });
    });
  const isCancelled: FrameRouting['isCancelled'] =
    options.routing?.isCancelled ?? (() => false);
  const isFrameForActiveSession: FrameRouting['isFrameForActiveSession'] =
    options.routing?.isFrameForActiveSession ?? (() => true);
  const onRunEnd: (runId: string) => void =
    options.routing?.onRunEnd ?? (() => undefined);
  if (frame.type === 'event') {
    const evt = frame;

    // Generative UI render directive.
    if (evt.event === 'ui.render') {
      if (!options.generativeUiEnabled) {
        setState((prev) => ({
          ...prev,
          processing: null,
          messages: [
            ...closeStreamingMessages(prev.messages),
            {
              id: generateMessageId('ui-disabled'),
              role: 'assistant' as const,
              content:
                '[Agent wanted to render UI — enable the terminal-generative-ui flag]',
              timestamp: Date.now(),
            },
          ],
        }));
        return;
      }

      const ui = evt.payload as UiRender | undefined;
      if (ui == null) return;
      const mode = ui.mode as string;
      if (mode !== 'catalog' && mode !== 'mcp-app') return;

      const messageId = generateMessageId('ui');
      const message: ChatMessage = {
        id: messageId,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        ui,
      };

      setState((prev) => {
        const next: AgentState = {
          ...prev,
          processing: null,
          messages: [...closeStreamingMessages(prev.messages), message],
        };
        if ((ui.placement as string | undefined) === 'full-terminal') {
          return { ...next, fullTerminalUi: { messageId, ui } };
        }
        return next;
      });
      return;
    }

    // Handle app.register — register a third-party app tab
    if (evt.event === 'app.register') {
      const payload = evt.payload as
        | {
            id?: string;
            name?: string;
            icon?: string;
            url?: string;
            permissions?: string[];
          }
        | undefined;
      if (
        payload?.id != null &&
        payload.id !== 'agent-dashboard' &&
        payload.name != null &&
        payload.url != null &&
        // Reject non-http(s) URLs (e.g. javascript:, data:) before they
        // reach the app iframe `src` — the payload is untrusted gateway input.
        isSafeAppUrl(payload.url)
      ) {
        const manifest: AppManifest = {
          id: payload.id,
          name: payload.name,
          icon: payload.icon,
          url: payload.url,
          permissions: payload.permissions ?? [],
        };
        options.onAppRegister?.(manifest);
      }
      return;
    }

    // Handle app.remove — remove a third-party app tab
    if (evt.event === 'app.remove') {
      const payload = evt.payload as { id?: string } | undefined;
      if (payload?.id != null) {
        options.onAppRemove?.(payload.id);
      }
      return;
    }

    // Handle "agent" events — streamed assistant responses
    if (evt.event === 'agent') {
      const payload = evt.payload as
        | {
            stream?: string;
            data?: {
              text?: string;
              delta?: string;
              state?: string;
              errorMessage?: string;
              tool?: string;
              toolName?: string;
              widget?: {
                type: string;
                data: Record<string, unknown>;
              };
            };
            runId?: string;
            sessionKey?: string;
          }
        | undefined;

      if (payload == null) return;

      const { data } = payload;
      if (data == null) return;

      // Error state
      if (data.state === 'error') {
        if (payload.runId != null && isCancelled(payload.runId)) {
          return;
        }
        const { errorMessage } = data;
        routeMessageFrame(
          payload,
          (messages) => [
            ...closeStreamingMessages(messages, payload.runId),
            {
              id: generateMessageId('error'),
              role: 'assistant' as const,
              content: `Error: ${errorMessage ?? 'Unknown error'}`,
              timestamp: Date.now(),
            },
          ],
          { processing: null },
        );
        if (payload.runId != null) onRunEnd(payload.runId);
        return;
      }

      // Widget payload — render as a structured widget
      if (data.widget != null) {
        if (payload.runId != null && isCancelled(payload.runId)) {
          return;
        }
        const { widget } = data;
        const widgetText = data.text;
        // agent_spec widget — render via AgentRenderer
        if (widget.type === 'agent_spec') {
          routeMessageFrame(
            payload,
            (messages) => [
              ...closeStreamingMessages(messages, payload.runId),
              {
                id: generateMessageId('agent-spec'),
                role: 'assistant' as const,
                content: '',
                timestamp: Date.now(),
                agentSpec: widget.data as unknown,
              },
            ],
            { processing: null },
          );
          return;
        }

        routeMessageFrame(
          payload,
          (messages) => [
            ...closeStreamingMessages(messages, payload.runId),
            {
              id: generateMessageId('widget'),
              role: 'assistant' as const,
              content: widgetText ?? '',
              timestamp: Date.now(),
              widgetType: widget.type,
              widgetData: widget.data,
            },
          ],
          { processing: null },
        );
        return;
      }

      // Streaming assistant text — use delta to update the last message
      if (payload.stream === 'assistant' && data.delta != null) {
        const { runId } = payload;
        if (runId != null && isCancelled(runId)) {
          return;
        }
        const suppressed = options.suppressedModelEchoRunIds;
        if (runId != null && suppressed?.has(runId) === true) {
          // First delta on this run was a `/model` echo; drop the rest.
          return;
        }
        const deltaText = data.delta;
        routeMessageFrame(
          payload,
          (messages) => {
            const updated = updateStreamingMessage(
              messages,
              runId,
              (message) => ({
                ...message,
                content: message.content + deltaText,
              }),
            );
            if (updated != null) return updated;
            // First delta of a new streaming message. If it looks like
            // the gateway's `/model <id>` echo, skip creating the
            // bubble — the picker already added an optimistic
            // acknowledgment in `setSelectedModel`. Mark the runId so
            // subsequent deltas for the same run also get dropped.
            if (isModelSwitchEcho(deltaText)) {
              if (runId != null) suppressed?.add(runId);
              return messages;
            }
            return [
              ...(runId == null ? closeStreamingMessages(messages) : messages),
              {
                id: generateMessageId('assistant'),
                role: 'assistant' as const,
                content: deltaText,
                timestamp: Date.now(),
                isStreaming: true,
                runId,
              },
            ];
          },
          { processing: null },
        );
        return;
      }

      // Processing-state indicator. The gateway sends `data.state` strings
      // like `thinking`, `tool_use`, `planning`, etc. We don't have the
      // canonical vocabulary locally (OpenClaw lives in a sibling repo), so
      // we map known values to friendly labels and titleize anything else —
      // the UI degrades gracefully as the gateway adds new states.
      if (typeof data.state === 'string' && data.state.length > 0) {
        const stateKey = data.state.toLowerCase();
        if (TERMINAL_PROCESSING_STATES.has(stateKey)) {
          // Terminal state is also our most reliable stream-end signal —
          // some gateway builds skip `agent.done`. Flip any streaming
          // message for this run off so the dots stop animating.
          routeMessageFrame(
            payload,
            (messages) => closeStreamingMessages(messages, payload.runId),
            { processing: null },
          );
          if (payload.runId != null) onRunEnd(payload.runId);
          return;
        }
        const label = resolveProcessingLabel(data.state);
        const rawDetail = data.toolName ?? data.tool ?? data.text;
        const detail =
          rawDetail != null && rawDetail.length > 0 ? rawDetail : undefined;
        if (!isFrameForActiveSession(payload)) {
          return;
        }
        setState((prev) => {
          const lastMsg = prev.messages[prev.messages.length - 1];
          if (lastMsg?.isStreaming === true) return prev;
          return {
            ...prev,
            processing: { label, detail },
          };
        });
        return;
      }

      return;
    }

    // Handle "chat" events — structured message delivery (delta / final / error).
    if (evt.event === 'chat') {
      const payload = evt.payload as
        | {
            state?: string;
            text?: string;
            errorMessage?: string;
            runId?: string;
            sessionKey?: string;
            message?: {
              role?: string;
              content?: ReadonlyArray<{ type?: string; text?: string }>;
              timestamp?: number;
            };
          }
        | undefined;

      if (payload?.state === 'error') {
        if (payload.runId != null && isCancelled(payload.runId)) {
          return;
        }
        const { errorMessage } = payload;
        routeMessageFrame(
          payload,
          (messages) => [
            ...closeStreamingMessages(messages, payload.runId),
            {
              id: generateMessageId('error'),
              role: 'assistant' as const,
              content: `Error: ${errorMessage ?? 'Unknown error'}`,
              timestamp: Date.now(),
            },
          ],
          { processing: null },
        );
        if (payload.runId != null) onRunEnd(payload.runId);
        return;
      }

      // Extract text from the structured message content array.
      const msgContent = payload?.message?.content;
      if (msgContent != null && msgContent.length > 0) {
        const text = msgContent
          .filter((c) => c.type === 'text' && c.text != null)
          .map((c) => c.text!)
          .join('');

        if (text.length > 0) {
          const runId = payload?.runId;
          const ts = payload?.message?.timestamp ?? Date.now();

          if (runId != null && isCancelled(runId)) {
            return;
          }

          // Gateway `/model <id>` echo — the picker already added an
          // optimistic acknowledgment client-side, so drop the duplicate.
          // `chat` event text is cumulative (each delta carries the full
          // message so far), so the prefix check matches reliably even
          // mid-stream. Also remove any streaming bubble we created from
          // an earlier delta before we recognized the prefix.
          const suppressed = options.suppressedModelEchoRunIds;
          if (isModelSwitchEcho(text) && payload != null) {
            if (runId != null) suppressed?.add(runId);
            routeMessageFrame(
              payload,
              (messages) =>
                runId == null
                  ? messages
                  : messages.filter(
                      (m) =>
                        !(
                          m.role === 'assistant' &&
                          m.runId === runId &&
                          m.isStreaming === true
                        ),
                    ),
              { processing: null },
            );
            if (payload.state === 'final' && runId != null) onRunEnd(runId);
            return;
          }
          if (runId != null && suppressed?.has(runId) === true) {
            return;
          }

          if (payload?.state === 'delta') {
            routeMessageFrame(
              payload,
              (messages) => {
                const updated = updateStreamingMessage(
                  messages,
                  runId,
                  (message) => ({ ...message, content: text }),
                );
                if (updated != null) return updated;
                return [
                  ...(runId == null
                    ? closeStreamingMessages(messages)
                    : messages),
                  {
                    id: generateMessageId('chat-delta'),
                    role: 'assistant' as const,
                    content: text,
                    timestamp: ts,
                    isStreaming: true,
                    runId,
                  },
                ];
              },
              { processing: null },
            );
            return;
          }

          if (payload?.state === 'final') {
            routeMessageFrame(
              payload,
              (messages) => {
                const replacements: ChatMessage[] = [];
                const extracted = tryExtractAgentSpec(text);
                if (extracted != null) {
                  if (extracted.preamble.length > 0) {
                    replacements.push({
                      id: generateMessageId('chat-text'),
                      role: 'assistant' as const,
                      content: extracted.preamble,
                      timestamp: ts,
                      runId,
                    });
                  }
                  replacements.push({
                    id: generateMessageId('agent-spec'),
                    role: 'assistant' as const,
                    content: '',
                    timestamp: ts,
                    runId,
                    agentSpec: extracted.spec,
                  });
                  return replaceRunMessages(messages, runId, replacements);
                }
                replacements.push({
                  id: generateMessageId('chat-final'),
                  role: 'assistant' as const,
                  content: text,
                  timestamp: ts,
                  runId,
                });
                return replaceRunMessages(messages, runId, replacements);
              },
              { processing: null },
            );
            return;
          }
        }
      }
      return;
    }

    // Handle structured approval request events from gateway
    if (
      evt.event === 'agent.approval' ||
      evt.event === 'approval.request' ||
      evt.event === 'tool.approval'
    ) {
      const payload = evt.payload as
        | { id?: string; command?: string; description?: string }
        | undefined;
      if (payload?.command != null) {
        const approval: ApprovalRequest = {
          approvalId: payload.id ?? generateMessageId('approval'),
          command: payload.command,
          description: payload.description,
          state: 'pending',
        };
        setState((prev) => ({
          ...prev,
          processing: null,
          messages: [
            ...closeStreamingMessages(prev.messages),
            {
              id: generateMessageId('approval'),
              role: 'assistant' as const,
              content: payload.description ?? `Approve: ${payload.command}`,
              timestamp: Date.now(),
              approval,
            },
          ],
        }));
      }
      return;
    }

    // Handle "chat.done" / "agent.done" — mark streaming as complete.
    // Also detect text-based approval requests in completed messages.
    if (evt.event === 'agent.done' || evt.event === 'chat.done') {
      const payload = evt.payload as
        | { runId?: string; sessionKey?: string }
        | undefined;
      const runId = payload?.runId;
      if (runId != null && isCancelled(runId)) {
        return;
      }
      routeMessageFrame(
        payload ?? {},
        (messages) => {
          const closed = closeStreamingMessages(messages, runId);
          let targetIndex = closed.length - 1;
          if (runId != null) {
            targetIndex = -1;
            for (let i = closed.length - 1; i >= 0; i -= 1) {
              const message = closed[i];
              if (message?.role === 'assistant' && message.runId === runId) {
                targetIndex = i;
                break;
              }
            }
          }
          const targetMsg = targetIndex >= 0 ? closed[targetIndex] : undefined;

          if (
            targetMsg != null &&
            targetMsg.role === 'assistant' &&
            targetMsg.agentSpec == null
          ) {
            const extracted = tryExtractAgentSpec(targetMsg.content);
            if (extracted != null) {
              const replacements: ChatMessage[] = [];
              if (extracted.preamble.length > 0) {
                replacements.push(
                  {
                    ...targetMsg,
                    content: extracted.preamble,
                    agentSpec: undefined,
                  } as ChatMessage,
                  {
                    id: generateMessageId('agent-spec'),
                    role: 'assistant' as const,
                    content: '',
                    timestamp: targetMsg.timestamp,
                    runId: targetMsg.runId,
                    agentSpec: extracted.spec,
                  },
                );
              } else {
                replacements.push({
                  id: generateMessageId('agent-spec'),
                  role: 'assistant' as const,
                  content: '',
                  timestamp: targetMsg.timestamp,
                  runId: targetMsg.runId,
                  agentSpec: extracted.spec,
                });
              }
              return [
                ...closed.slice(0, targetIndex),
                ...replacements,
                ...closed.slice(targetIndex + 1),
              ];
            }

            if (targetMsg.approval == null) {
              const approval = detectApprovalRequest(targetMsg.content);
              if (approval != null) {
                return [
                  ...closed.slice(0, targetIndex),
                  { ...targetMsg, approval },
                  ...closed.slice(targetIndex + 1),
                ];
              }
            }
          }
          return closed;
        },
        { processing: null },
      );
      if (runId != null) onRunEnd(runId);
      return;
    }

    // Silently ignore other events (health, tick, presence, etc.)
    return;
  }

  // At this point frame must be a GatewayResponse (events handled above).
  // Responses whose id matched a pending request were already resolved/rejected
  // in createAgentWebSocket (never reaches here). What arrives here are
  // "orphan" error responses — typically infrastructure-level failures relayed
  // by the gateway-proxy (e.g. "gateway unreachable") that have no matching
  // in-flight request. These are connection-layer concerns, not conversation
  // content — the reconnect machinery and status banners already surface them.
  // Rendering them as chat bubbles pollutes the conversation, so drop them.
  {
    const res = frame;
    if (!res.ok && res.error != null) {
      const msg = res.error.message ?? 'Request failed';
      // eslint-disable-next-line no-console
      console.warn(
        '[terminal] orphan error response (not rendered):',
        msg,
        res,
      );
      // Surface as a transient error (auto-dismissed) + clear processing.
      setState((prev) => ({
        ...prev,
        processing: null,
        transientError: msg,
      }));
      options.onTransientError?.(msg);
    }
  }
}

/**
 * Try to extract a JSON agent-spec from streamed message content.
 *
 * The LLM sometimes emits the component-tree JSON inline as plain text
 * (instead of a structured `widget` frame). When that happens the chat
 * renders raw JSON. This helper finds the outermost `{…}` blob whose
 * shape matches an agent spec (`layout`, `children`) and returns it
 * together with any leading prose so the caller can split the message.
 * The spec is opaque (`unknown`) — the host renderer narrows it.
 */
function tryExtractAgentSpec(
  text: string,
): { spec: unknown; preamble: string } | null {
  // Find the first top-level '{' that could start a JSON object.
  const start = text.indexOf('{');
  if (start === -1) return null;

  // Walk from the *end* to find the matching closing '}'.
  const end = text.lastIndexOf('}');
  if (end <= start) return null;

  const candidate = text.slice(start, end + 1);
  try {
    const parsed: unknown = JSON.parse(candidate);
    if (
      parsed != null &&
      typeof parsed === 'object' &&
      'layout' in (parsed as Record<string, unknown>) &&
      'children' in (parsed as Record<string, unknown>) &&
      Array.isArray((parsed as Record<string, unknown>).children)
    ) {
      return {
        spec: parsed,
        preamble: text.slice(0, start).trim(),
      };
    }
  } catch {
    // Not valid JSON — fall through.
  }
  return null;
}

/**
 * Detect approval-request patterns in agent text messages.
 * Matches patterns like:
 *   "please approve the `some command` command"
 *   "approval needed — please approve the `...` command in the UI"
 * Extracts the command from backticks.
 */
function detectApprovalRequest(text: string): ApprovalRequest | null {
  // Match "approve" near a backtick-wrapped command
  const approvePattern =
    /(?:please\s+)?approv(?:e|al\s+(?:needed|required))[^`]*`([^`]+)`/iu;
  const match = approvePattern.exec(text);
  if (match != null) {
    const desc = text.replace(approvePattern, '').trim();
    return {
      approvalId: generateMessageId('text-approval'),
      command: match[1] ?? '',
      description: desc.length > 0 ? desc : undefined,
      state: 'pending',
    };
  }
  return null;
}
